<template>
  <div class="page-wrapper">
    <a-breadcrumb style="margin: 16px 0">
      <a-breadcrumb-item>VOD维度</a-breadcrumb-item>
      <a-breadcrumb-item>概览</a-breadcrumb-item>
    </a-breadcrumb>
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">VOD维度概览</h2>
    </div>

    <!-- 统计概览 -->
    <div class="stat-row">
      <div
        v-for="card in topStatusCards"
        :key="card.label"
        class="stat-card top-metric-card"
      >
        <div class="stat-label">{{ card.label }}</div>
        <div class="stat-value" :class="card.className">{{ formatNumber(card.value) }}</div>
      </div>
    </div>

    <div class="insight-grid">
      <div class="section-card chart-card">
        <div class="section-card-header">
          <span class="section-card-title">TOP VOD问题领域</span>
        </div>
        <div ref="barChartRef" class="chart-body"></div>
      </div>

      <div class="section-card progress-section">
        <div class="section-card-header">
          <span class="section-card-title">类别闭环进展（按问题类别）</span>
        </div>
        <div class="progress-chart-wrap">
          <VodCategoryProgressChart :rows="closureProgressRows" />
        </div>
      </div>
    </div>

    <div class="secondary-grid">
      <div class="section-card cycle-card">
        <div class="section-card-header">
          <span class="section-card-title">VOD平均处理周期</span>
        </div>
        <div class="cycle-list">
          <div v-for="item in cycleItems" :key="item.label" class="cycle-item">
            <div class="cycle-label-text">{{ item.label }}</div>
            <div class="cycle-bar-wrap">
              <div class="cycle-bar" :style="{ width: item.pct + '%', background: item.color }"></div>
            </div>
            <div class="cycle-days">{{ item.days }}天</div>
          </div>
        </div>
      </div>

      <div class="section-card image-card">
        <div class="section-card-header">
          <span class="section-card-title">VOD关键词云</span>
        </div>
        <div class="image-body">
          <img v-if="imageUrl" :src="imageUrl" alt="关键词云" class="cloud-img" />
          <div v-else class="img-placeholder">请替换图片地址（修改 imageUrl 变量）</div>
        </div>
      </div>
    </div>

    <!-- 下半：两个表格 -->
    <div class="table-row">
      <!-- TOP典型VOD问题 -->
      <div class="section-card table-section">
        <div class="section-card-header">
          <span class="section-card-title">TOP典型VOD问题</span>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th style="width:50px;">序号</th>
                <th style="width:120px;">问题类别</th>
                <th>高频关键词统计</th>
                <th>主要表现</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in typicalData" :key="idx" :class="{ 'row-odd': idx % 2 === 0 }">
                <td class="center-cell">
                  <span class="index-badge">{{ row.index }}</span>
                </td>
                <td class="type-cell">{{ row.type }}</td>
                <td class="keyword-cell">{{ row.keywords }}</td>
                <td>{{ row.performance }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 子系统缺陷率统计 -->
    <div class="section-card table-section" style="margin-bottom: 24px;">
      <div class="section-card-header">
        <span class="section-card-title">子系统缺陷率统计</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>子系统</th>
              <th style="text-align:right;">总问题数</th>
              <th style="text-align:right;">缺陷数</th>
              <th style="text-align:center;">缺陷率</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in defectData" :key="idx" :class="{ 'row-odd': idx % 2 === 0 }">
              <td>{{ row.system }}</td>
              <td style="text-align:right;">{{ row.total.toLocaleString() }}</td>
              <td style="text-align:right;">{{ row.defect.toLocaleString() }}</td>
              <td style="text-align:center;">
                <span class="rate-badge">{{ row.rate }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import * as echarts from 'echarts';
import wordCloudImg from './wordCloud.png';
import VodCategoryProgressChart from '../components/VodCategoryProgressChart.vue';

const formatNumber = (num) => num.toLocaleString();
const imageUrl = ref(wordCloudImg);

const sourceRows = [
  { label: '任务', pendingClarify: 0, preparing: 59618, inProgress: 1510, verifying: 0, closed: 271864, total: 332992 },
  { label: '缺陷', pendingClarify: 123, preparing: 19794, inProgress: 967, verifying: 32, closed: 206885, total: 227797 },
  { label: '需求', pendingClarify: 10, preparing: 16199, inProgress: 232, verifying: 14, closed: 24983, total: 41438 },
  { label: '安全问题', pendingClarify: 0, preparing: 195, inProgress: 237, verifying: 0, closed: 18020, total: 18452 },
  { label: '咨询', pendingClarify: 1085, preparing: 0, inProgress: 5, verifying: 0, closed: 540, total: 1630 },
  { label: '安全漏洞', pendingClarify: 0, preparing: 16, inProgress: 22, verifying: 0, closed: 112, total: 150 },
  { label: '总计', pendingClarify: 1218, preparing: 96016, inProgress: 2973, verifying: 46, closed: 522409, total: 622662 },
];

const summaryRow = computed(() => sourceRows.find((item) => item.label === '总计') || sourceRows[sourceRows.length - 1]);

const topStatusCards = computed(() => [
  { label: '意向、待确认、已确认', value: summaryRow.value.pendingClarify, className: 'highlight-orange' },
  { label: '准备中', value: summaryRow.value.preparing, className: 'highlight-slate' },
  { label: '进行中', value: summaryRow.value.inProgress, className: 'highlight-blue' },
  { label: '已修复、验收中、已验收', value: summaryRow.value.verifying, className: 'highlight-indigo' },
  { label: '已闭环', value: summaryRow.value.closed, className: 'highlight-green' },
]);

const categoryRows = computed(() => sourceRows.filter((item) => item.label !== '总计'));

const categorySum = computed(() => {
  return categoryRows.value.reduce((accumulator, item) => {
    accumulator.pendingClarify += item.pendingClarify;
    accumulator.preparing += item.preparing;
    accumulator.inProgress += item.inProgress;
    accumulator.verifying += item.verifying;
    accumulator.closed += item.closed;
    accumulator.total += item.total;
    return accumulator;
  }, {
    pendingClarify: 0,
    preparing: 0,
    inProgress: 0,
    verifying: 0,
    closed: 0,
    total: 0,
  });
});

const summaryDelta = computed(() => ({
  pendingClarify: summaryRow.value.pendingClarify - categorySum.value.pendingClarify,
  preparing: summaryRow.value.preparing - categorySum.value.preparing,
  inProgress: summaryRow.value.inProgress - categorySum.value.inProgress,
  verifying: summaryRow.value.verifying - categorySum.value.verifying,
  closed: summaryRow.value.closed - categorySum.value.closed,
  total: summaryRow.value.total - categorySum.value.total,
}));

const hasSummaryMismatch = computed(() => Object.values(summaryDelta.value).some((value) => value !== 0));

const closureProgressRows = computed(() => {
  return sourceRows
    .filter((item) => item.label !== '总计')
    .map((item) => {
      const total = item.total || 1;
      const rawClarifyPct = (item.pendingClarify / total) * 100;
      const rawPreparePct = (item.preparing / total) * 100;
      const rawOngoingPct = (item.inProgress / total) * 100;
      const rawVerifyPct = (item.verifying / total) * 100;
      const rawClosePct = (item.closed / total) * 100;

      const minVisiblePct = (rawPct, value) => {
        if (value <= 0) return 0;
        return Math.max(rawPct, 0.6);
      };

      const closeRateValue = rawClosePct.toFixed(1);
      return {
        label: item.label,
        pendingClarify: item.pendingClarify,
        preparing: item.preparing,
        inProgress: item.inProgress,
        verifying: item.verifying,
        closed: item.closed,
        total: item.total,
        clarifyPctRaw: rawClarifyPct.toFixed(1),
        preparePctRaw: rawPreparePct.toFixed(1),
        ongoingPctRaw: rawOngoingPct.toFixed(1),
        verifyPctRaw: rawVerifyPct.toFixed(1),
        closePctRaw: rawClosePct.toFixed(1),
        clarifyPct: minVisiblePct(rawClarifyPct, item.pendingClarify).toFixed(3),
        preparePct: minVisiblePct(rawPreparePct, item.preparing).toFixed(3),
        ongoingPct: minVisiblePct(rawOngoingPct, item.inProgress).toFixed(3),
        verifyPct: minVisiblePct(rawVerifyPct, item.verifying).toFixed(3),
        closePct: minVisiblePct(rawClosePct, item.closed).toFixed(3),
        closeRate: closeRateValue,
      };
    });
});

// 周期数据
const cycleItems = [
  { label: '咨询类',   days: 55, pct: 100, color: '#1677ff' },
  { label: '安全漏洞', days: 49, pct: 89,  color: '#fa8c16' },
  { label: '需求',     days: 27, pct: 49,  color: '#1677ff' },
  { label: '任务类',   days: 27, pct: 49,  color: '#52c41a' },
  { label: '缺陷类',   days: 22, pct: 40,  color: '#f5222d' },
];

const barChartRef = ref(null);
const initBarChart = () => {
  const chartInstance = echarts.init(barChartRef.value);
  const option = {
    devicePixelRatio: window.devicePixelRatio || 2,
    grid: { left: '4%', right: '4%', bottom: '4%', top: '6%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['ArkUI开发框架', '开发框架与\n北向生态配套', '语言运行时\n和基础库', '图形平台', '互联平台', '测试与认证', '安全基础平台', '媒体平台', '南向硬件平台', '全球化与DFX'],
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#8c8c8c', fontSize: 11, rotate: -30, interval: 0 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f0f0f0' } },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#8c8c8c', fontSize: 11 },
    },
    series: [{
      data: [72560, 56917, 40824, 30810, 27796, 26594, 25991, 23804, 20295, 20262],
      type: 'bar',
      itemStyle: {
        color: (params) => {
          const colors = ['#1677ff','#40a9ff','#69b8f7','#95ccfa','#a8d8fb','#b8e0fc','#c8e8fd','#d5edfe','#e0f2fe','#ecf7ff'];
          return colors[params.dataIndex] || '#1677ff';
        },
        borderRadius: [4, 4, 0, 0]
      },
      barWidth: '50%',
      label: { show: true, position: 'top', color: '#595959', fontSize: 10, formatter: (p) => (p.value / 1000).toFixed(1) + 'k' }
    }],
    tooltip: { trigger: 'axis', formatter: '{b}: {c}' }
  };
  chartInstance.setOption(option);
  window.addEventListener('resize', () => chartInstance.resize());
};

const typicalData = ref([
  { index: 1, type: '构建/编译类', keywords: '失败30k+、构建20k+、编译10k+', performance: '编译失败、构建异常依赖缺失导致流程中断' },
  { index: 2, type: '功能异常类', keywords: '错误11k+、异常8k+、无法5k+', performance: '核心功能失效、接口报错返回结果不符合预期' },
  { index: 3, type: '性能体验类', keywords: '卡顿5k+、崩溃2k+、闪退1k+', performance: '界面操作卡顿、响应缓慢应用运行时崩溃或闪退' },
  { index: 4, type: '接口使用类', keywords: '调用8k+、参数6k+、超时4k+', performance: '接口调用失败、参数传递错误请求超时或返回值异常' },
  { index: 5, type: '适配兼容类', keywords: '适配9k+、不兼容8k+、设备5k+', performance: '设备/架构适配异常第三方库/组件无法兼容' },
  { index: 6, type: '权限安全类', keywords: '权限7k+、漏洞6k+、泄露5k+', performance: '权限申请无效、安全漏洞风险隐私数据泄露或权限管控失效' },
  { index: 7, type: '界面交互类', keywords: '显示6k+、操作4k+、性能2k+', performance: '控件显示异常、手势操作低效弹窗/软键盘遮挡界面' },
  { index: 8, type: '分布式能力类', keywords: '组网5k+、同步4k+、迁移3k+', performance: '分布式组网失败、跨设备数据不同步设备迁移/连接功能失效' },
  { index: 9, type: '文档配置类', keywords: '错误6k+、缺失4k+、复杂3k+', performance: '文档描述不一致、配置流程复杂参数说明缺失或路径错误' },
  { index: 10, type: '测试工具类', keywords: '用例5k+、失败4k+、无法2k+', performance: 'XTS用例执行失败、调试工具异常测试环境搭建流程受阻' }
]);

const defectData = ref([
  { system: '输入法', total: 1697, defect: 1243, rate: '73.2%' },
  { system: 'USB服务', total: 1002, defect: 730, rate: '72.9%' },
  { system: '分布式总线', total: 9591, defect: 6633, rate: '69.4%' },
  { system: '事件通知', total: 4148, defect: 2712, rate: '65.4%' },
  { system: '窗口', total: 12481, defect: 7942, rate: '63.6%' },
  { system: '电源服务', total: 2918, defect: 1840, rate: '63.6%' },
  { system: '资源调度', total: 2340, defect: 1455, rate: '61.3%' },
  { system: 'OS媒体软件', total: 36731, defect: 22530, rate: '61.5%' },
  { system: '分布式数据管理', total: 10580, defect: 6350, rate: '60.0%' },
  { system: '卡片框架', total: 2447, defect: 1440, rate: '58.8%' }
]);

onMounted(() => {
  initBarChart();
  if (hasSummaryMismatch.value) {
    console.warn('[VodOverview] CSV 分类汇总与总计存在差异:', summaryDelta.value);
  }
});
</script>

<style scoped>
/* ===== 页面整体 ===== */
.page-wrapper {
  padding: 20px 24px;
  background: #ffffff;
  min-height: 100%;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== 页面标题 ===== */
.page-header { margin-bottom: 4px; }
.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
  padding-left: 12px;
  border-left: 4px solid #1677ff;
}

/* ===== 统计卡片行 ===== */
.stat-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
}
.stat-card {
  min-width: 0;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #dbe5f0;
  padding: 16px 18px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.stat-label { font-size: 14px; color: #60748f; font-weight: 600; }
.stat-value {
  font-size: 36px;
  font-weight: 700;
  color: #14213d;
  line-height: 1.2;
}
.stat-value.highlight-green { color: #52c41a; }
.stat-value.highlight-orange { color: #fa8c16; }
.stat-value.highlight-blue { color: #4f8df6; }
.stat-value.highlight-slate { color: #6f829b; }
.stat-value.highlight-indigo { color: #5b6ce1; }
.top-metric-card { min-height: 148px; justify-content: center; }

/* ===== 通用 section 卡片 ===== */
.section-card {
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  overflow: hidden;
}

.progress-section {
  border-radius: 14px;
  border: 1px solid #dbe5f0;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  height: 430px;
  display: flex;
  flex-direction: column;
}

.progress-chart-wrap {
  flex: 1;
  min-height: 0;
  padding: 8px 8px 10px;
}

.section-card-header {
  display: flex;
  align-items: center;
  padding: 14px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
}
.section-card-title {
  font-size: 17px;
  font-weight: 700;
  color: #202f45;
}

/* ===== 中部布局 ===== */
.insight-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(520px, 1fr);
  gap: 18px;
  align-items: stretch;
}

.chart-card {
  border: 1px solid #dbe5f0;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  height: 430px;
  display: flex;
  flex-direction: column;
}

.secondary-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
}

.chart-card {
  min-width: 0;
}
.cycle-card {
  min-width: 0;
}
.chart-body {
  width: 100%;
  flex: 1;
  min-height: 0;
}

/* ===== 周期列表 ===== */
.cycle-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px;
}
.cycle-item {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cycle-label-text {
  width: 70px;
  font-size: 13px;
  color: #595959;
  flex-shrink: 0;
}
.cycle-bar-wrap {
  flex: 1;
  height: 8px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}
.cycle-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.4s ease;
}
.cycle-days {
  width: 44px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  flex-shrink: 0;
}

/* ===== 关键词云 ===== */
.image-card .image-body {
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.cloud-img { width: 100%; height: 100%; object-fit: contain; }
.img-placeholder { font-size: 14px; color: #bfbfbf; }

/* ===== 表格区 ===== */
.table-row { display: flex; flex-direction: column; }
.table-wrap { width: 100%; overflow-x: auto; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.data-table thead tr { background: #fafafa; }
.data-table th {
  padding: 11px 16px;
  font-weight: 600;
  color: #595959;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  text-align: left;
}
.data-table td {
  padding: 10px 16px;
  color: #262626;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
}
.data-table tr:last-child td { border-bottom: none; }
.data-table tr:hover td { background: #e6f4ff; }
.row-odd td { background: #fafcff; }
.center-cell { text-align: center; }

.index-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e6f4ff;
  color: #1677ff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
}

.type-cell {
  white-space: nowrap;
  font-weight: 500;
  color: #262626;
}
.keyword-cell {
  color: #595959;
  white-space: nowrap;
}

.rate-badge {
  display: inline-block;
  padding: 2px 10px;
  background: linear-gradient(135deg, #ff4d4f, #f5222d);
  color: #fff;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

/* ===== 响应式 ===== */
@media (max-width: 1100px) {
  .insight-grid,
  .secondary-grid { grid-template-columns: 1fr; }
  .stat-row { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .stat-value { font-size: 32px; }
  .progress-section,
  .chart-card { height: auto; }
  .section-card-title { font-size: 16px; }
}

@media (max-width: 680px) {
  .stat-row { grid-template-columns: 1fr; }
}
</style>